import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  ListItem,
  Text,
  Textarea,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import { visibleLength } from "../../../common/text/visibleLength";
import moment from "moment";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type SourcingEventDto, useInviteToConfirmInterestMutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { ThemedDateTimePicker } from "../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { eeaPeriodEnd } from "../../../common/procurement/eeaTimeLimits";
import { interestRequestFloor, usesAcceleratedProcedure } from "../../sourcing-events/buyer/wizard/utils/karens";
import { priorInformationCallProvision } from "../../../common/procurement/priorInformationCall";

interface Props {
  event: SourcingEventDto;
}

type Window = "unsent" | "notYet" | "open" | "lapsed";

export const InterestInvitationCard = ({ event }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const [invite, { isLoading }] = useInviteToConfirmInterestMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const call = event.eventContext.type === "Public" ? event.eventContext.priorInformationCall : null;
  const registrations = event.supplierRegistrations ?? [];
  const regulation = event.eventContext.type === "Public" ? event.eventContext.regulation : undefined;
  const urgent = event.eventContext.type === "Public" && usesAcceleratedProcedure(event.eventContext);
  const floor = interestRequestFloor(regulation, urgent);
  const sentAt = invitationSentAt(call?.earliestInvitationAt);
  const earliestDeadline = eeaPeriodEnd(sentAt, floor.minDays);
  const generalRuleDeadline = eeaPeriodEnd(sentAt, floor.generalRuleDays ?? floor.minDays);
  const [deadline, setDeadline] = useState<string>(
    generalRuleDeadline.clone().add(1, "day").set({ hours: 12, minutes: 0, seconds: 0 }).toISOString(),
  );
  const [reason, setReason] = useState("");
  if (!call) return null;

  const now = moment();
  const opens = call.earliestInvitationAt ? moment(call.earliestInvitationAt) : null;
  const closes = call.latestInvitationAt ? moment(call.latestInvitationAt) : null;
  const window: Window =
    opens == null || closes == null ? "unsent" : now.isBefore(opens) ? "notYet" : now.isAfter(closes) ? "lapsed" : "open";
  const deadlineTooSoon = moment(deadline).isBefore(earliestDeadline);
  const belowGeneralRule =
    floor.generalRuleDays != null && !deadlineTooSoon && moment(deadline).isBefore(generalRuleDeadline);
  const reasonMissing = belowGeneralRule && visibleLength(reason) === 0;
  const canInvite = window === "open" && registrations.length > 0 && !deadlineTooSoon && !reasonMissing;

  const send = async () => {
    try {
      await invite({
        eventId: event.id,
        interestInvitationRequestDto: {
          requestDeadline: deadline,
          shortDeadlineJustification: belowGeneralRule ? reason.trim() : undefined,
        },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder" mb="6">
      <CardBody>
        <Heading as="h3" size="sm" color="smText" mb="1">
          {t("Start from the prior information notice")}
        </Heading>
        <Text fontSize="sm" color="smMuted" mb="5">
          {t(
            "The prior information notice is the call for competition ({{provision}}). Invite the suppliers who registered interest to confirm it by requesting to participate. No new contract notice is sent.",
            { provision: priorInformationCallProvision(regulation) },
          )}
        </Text>

        {window === "unsent" && (
          <Alert status="info" borderRadius="md" fontSize="sm" mb="5">
            <AlertIcon />
            {t("The invitation can be sent once the prior information notice has gone to Doffin.")}
          </Alert>
        )}
        {window === "notYet" && opens && closes && (
          <Alert status="info" borderRadius="md" fontSize="sm" mb="5">
            <AlertIcon />
            {t("The invitation cannot be sent yet. You can send it from {{from}} until {{until}}.", {
              from: displayDateWithTime(opens.toISOString()),
              until: displayDateWithTime(closes.toISOString()),
            })}
          </Alert>
        )}
        {window === "open" && closes && (
          <Text fontSize="sm" mb="5">
            {t("You can send the invitation until {{until}}.", { until: displayDateWithTime(closes.toISOString()) })}
          </Text>
        )}
        {window === "lapsed" && (
          <Alert status="warning" borderRadius="md" fontSize="sm" mb="5">
            <AlertIcon />
            {t(
              "More than 12 months have passed since the prior information notice was sent, so it can no longer call the competition ({{provision}} c). Cancel the procurement and announce it again.",
              { provision: priorInformationCallProvision(regulation) },
            )}
          </Alert>
        )}

        <Text fontSize="sm" fontWeight="semibold" mb="1">
          {t("Suppliers who registered interest ({{count}})", { count: registrations.length })}
        </Text>
        {registrations.length > 0 ? (
          <UnorderedList fontSize="sm" mb="5">
            {registrations.map((registration) => (
              <ListItem key={registration.organizationId}>{registration.organizationName}</ListItem>
            ))}
          </UnorderedList>
        ) : (
          <Text fontSize="sm" color="smMuted" mb="5">
            {t("No supplier has registered interest yet, so there is nobody to invite.")}
          </Text>
        )}

        {window !== "lapsed" && (
          <FormControl maxW="sm">
            <FormLabel fontSize="sm" htmlFor="interestRequestDeadline">
              {t("Deadline for requests to participate")}
            </FormLabel>
            <ThemedDateTimePicker
              id="interestRequestDeadline"
              date={deadline}
              onChange={(iso) => {
                if (iso) setDeadline(iso);
              }}
            />
            <FormHelperText>
              {floor.generalRuleDays != null
                ? t("{{generalDays}} days from the invitation as a rule, and at least {{minDays}} ({{provision}}).", {
                    generalDays: floor.generalRuleDays,
                    minDays: floor.minDays,
                    provision: floor.provision,
                  })
                : t("At least {{minDays}} days from the invitation ({{provision}}).", {
                    minDays: floor.minDays,
                    provision: floor.provision,
                  })}
            </FormHelperText>
            {deadlineTooSoon && (
              <Text mt="1" fontSize="sm" color="smError">
                {t("The deadline for requests to participate must be at least {{minDays}} days after the invitation.", {
                  minDays: floor.minDays,
                })}
              </Text>
            )}
          </FormControl>
        )}

        {window !== "lapsed" && belowGeneralRule && (
          <FormControl
            isRequired
            mt="4"
            p="3"
            borderRadius="md"
            bg="smWarningSoft"
            borderWidth="1px"
            borderColor="smWarningSoftBorder"
          >
            <FormLabel fontSize="sm" fontWeight="semibold" htmlFor="shortRequestDeadlineReason" mb="1">
              {t("Why the deadline for requests to participate is under {{generalDays}} days", {
                generalDays: floor.generalRuleDays,
              })}
            </FormLabel>
            <Text fontSize="sm" mb="2">
              {t(
                "{{generalDays}} days is the general rule in {{provision}}. A shorter deadline, down to {{minDays}} days, needs a reason. It goes into the procurement protocol.",
                {
                  generalDays: floor.generalRuleDays,
                  minDays: floor.minDays,
                  provision: floor.provision,
                },
              )}
            </Text>
            <Textarea
              id="shortRequestDeadlineReason"
              rows={3}
              bg="smBackground"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormControl>
        )}

        <Flex justify="flex-end" mt="5">
          <Button colorScheme="teal" onClick={onOpen} isDisabled={!canInvite}>
            {t("Start from the prior information notice")}
          </Button>
        </Flex>

        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t("Send the invitation to confirm interest?")}
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text fontSize="sm">
                  {t(
                    "{{count}} suppliers are invited now, with {{deadline}} as the deadline for requests to participate. Nobody else can register interest afterwards.",
                    { count: registrations.length, deadline: displayDateWithTime(deadline) },
                  )}
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose} variant="ghost">
                  {t("Cancel")}
                </Button>
                <Button colorScheme="teal" onClick={send} isLoading={isLoading} isDisabled={!canInvite} ml={3}>
                  {t("Send")}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </CardBody>
    </Card>
  );
};

const invitationSentAt = (earliestInvitationAt: string | null | undefined): moment.Moment => {
  const now = moment();
  const opens = earliestInvitationAt ? moment(earliestInvitationAt) : null;
  return opens && now.isBefore(opens) ? opens : now;
};
